import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, ArrowRight, Sparkles, Rocket } from "lucide-react";
import { OplyxLogo } from "@/components/brand/OplyxLogo";

/**
 * OnboardingOverlay — retomada da configuração, NÃO um wizard.
 *
 * Até aqui isto era um segundo wizard completo, com etapas próprias
 * (welcome/branch/product/fiscal/done) que não batiam com as do `/welcome`
 * (profile/catalog/payments/team) nem com os checkpoints que o backend calcula
 * em `/onboarding/status`. Três definições de "onboarding" para o mesmo cliente,
 * cada uma com sua contagem de progresso — e o cliente via um número diferente
 * dependendo de onde olhava.
 *
 * Agora existe um wizard só (`/welcome`, que absorveu o passo de endereço) e uma
 * só fonte de progresso (`/onboarding/status`, derivado das coleções reais).
 * Este componente apenas mostra o que falta e leva até lá.
 */
export default function OnboardingOverlay({ open, onClose, onComplete }) {
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/onboarding/status")
      .then(r => setStatus(r.data))
      .catch(() => { /* graceful: sem status, o overlay simplesmente não aparece */ });
  }, [open]);

  if (!open || !status) return null;

  const firstName = (() => {
    try {
      const u = JSON.parse(localStorage.getItem("oplyx_user") || "{}");
      return (u.name || "").split(" ")[0] || "você";
    } catch { return "você"; }
  })();

  // Ordem do roteiro; o backend é quem diz o que está feito.
  const ORDER = ["profile", "branch", "catalog", "payments", "team",
                 "printers", "fiscal_setup", "first_sale"];
  const items = ORDER
    .map(id => ({ id, ...(status.steps?.[id] || {}) }))
    .filter(s => s.title);
  const pending = items.filter(s => !s.done);
  const next = pending[0];

  const goToNext = () => {
    onClose?.();
    nav(next?.to || "/welcome");
  };

  const finish = async () => {
    setLoading(true);
    try {
      await api.post("/onboarding/complete", { force: true });
      onComplete?.();
    } catch {
      onClose?.();
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-lg" data-testid="onboarding-overlay">
        <div className="flex items-center gap-3">
          <OplyxLogo size={22} tone="auto" />
          <span className="text-xs tabular-nums text-muted-foreground ml-auto"
                data-testid="onboarding-overlay-pct">
            {status.completion_pct || 0}% configurado
          </span>
        </div>

        <div className="mt-3">
          <h2 className="text-xl font-semibold tracking-tight">
            {pending.length === 0
              ? `Tudo pronto, ${firstName}.`
              : `Bem-vindo, ${firstName}.`}
          </h2>
          <p className="text-muted-foreground text-sm mt-1">
            {pending.length === 0
              ? "Seu OPLYX está configurado. Pode fechar isto."
              : status.migration_intent === "migrating"
                ? "Falta pouco pra trazer sua operação inteira pra cá."
                : "Falta pouco pra você começar a vender."}
          </p>
        </div>

        <div className="mt-4 space-y-1.5">
          {items.map(s => (
            <button
              key={s.id}
              type="button"
              onClick={() => { onClose?.(); nav(s.to || "/welcome"); }}
              data-testid={`onboarding-overlay-step-${s.id}`}
              className="w-full flex items-center gap-2.5 text-left px-3 py-2 rounded-lg hover:bg-muted/60 transition-colors"
            >
              {s.done
                ? <CheckCircle2 className="w-4 h-4 text-success shrink-0" strokeWidth={2} />
                : <Circle className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={2} />}
              <span className={`text-sm truncate ${s.done ? "text-muted-foreground" : ""}`}>
                {s.title}
              </span>
              {!s.done && <ArrowRight className="w-3.5 h-3.5 text-muted-foreground ml-auto shrink-0" strokeWidth={2} />}
            </button>
          ))}
        </div>

        {status.migration_intent === "migrating" && (status.migration?.documents_uploaded || 0) === 0 && (
          <div className="mt-3 rounded-lg border border-sky-500/30 bg-sky-50 dark:bg-sky-500/10 px-3 py-2.5 text-sm text-sky-700 dark:text-sky-300">
            Você veio de outro sistema? Traga seus relatórios e a gente monta o catálogo
            e o histórico pra você.
          </div>
        )}

        <div className="mt-5 flex items-center gap-2">
          {pending.length > 0 ? (
            <Button onClick={goToNext} className="flex-1" data-testid="onboarding-overlay-continue">
              <Rocket className="w-4 h-4 mr-2" strokeWidth={2} />
              Continuar: {next?.title}
            </Button>
          ) : (
            <Button onClick={finish} disabled={loading} className="flex-1"
                    data-testid="onboarding-overlay-finish">
              <Sparkles className="w-4 h-4 mr-2" strokeWidth={2} />
              Concluir
            </Button>
          )}
          <Button variant="ghost" onClick={onClose} data-testid="onboarding-overlay-later">
            Depois
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
